@if (isLoading) {
  <div class="space-y-3">
    <hlm-skeleton class="h-9 w-48 rounded-lg" />
    <hlm-skeleton class="h-32 w-full rounded-lg" />
    <hlm-skeleton class="h-24 w-full rounded-lg" />
  </div>
}

@if (error) {
  <div hlmAlert variant="destructive" class="mb-3">
    <ng-icon name="lucideTriangleAlert" />
    <p hlmAlertTitle>{{ error }}</p>
  </div>
}

@if (successMessage && successVisible) {
  <div hlmAlert class="mb-3">
    <ng-icon name="lucideCircleCheck" />
    <p hlmAlertTitle>Eingelöst</p>
    <p hlmAlertDescription>{{ successMessage }}</p>
    <div hlmAlertAction>
      <button
        hlmBtn
        variant="ghost"
        size="icon-xs"
        aria-label="Hinweis schließen"
        (click)="successVisible = false">
        <ng-icon name="lucideX" />
      </button>
    </div>
  </div>
}

@if (voucher && !isLoading) {
  <!-- ── Code + status, above the tabs so it stays visible in both ── -->
  <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
    <p class="font-mono text-base font-medium">{{ voucher.code }}</p>
    <div class="flex flex-wrap items-center gap-1.5">
      <span hlmBadge [variant]="statusVariant">{{ statusLabel }}</span>
      <span hlmBadge variant="outline">
        {{ isProductVoucher() ? 'Produkt' : 'Wert' }}
      </span>
      @if (voucher.is_internal) {
        <span hlmBadge variant="secondary">Intern</span>
      }
    </div>
  </div>

  <hlm-tabs [tab]="activeTab" class="w-full">
    <hlm-tabs-list variant="line">
      <button hlmTabsTrigger="details" (click)="activeTab = 'details'">Details</button>
      <button hlmTabsTrigger="history" (click)="activeTab = 'history'">
        Historie
        @if (hasRedemptions) {
          <span hlmBadge variant="secondary" class="ms-1.5">
            {{ voucher.redemptions.length }}
          </span>
        }
      </button>
    </hlm-tabs-list>

    <!-- ═══════════════ Details ═══════════════ -->
    <div hlmTabsContent="details" class="min-h-80 space-y-4 pt-3">
      <!-- ── Status box: what is left, of how much, and how far along ── -->
      <div class="bg-muted/50 border-border space-y-3 rounded-lg border p-4">
        <p hlmMuted class="text-xs">{{ balanceLabel }}</p>

        <!-- Both figures share one baseline, so the comparison reads in one go -->
        <div class="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <p class="text-3xl leading-none font-bold tabular-nums">
            {{ formatAmount(voucher.amount) }} €
          </p>
          <p hlmMuted class="text-sm tabular-nums">
            von {{ formatAmount(voucher.initial_amount) }} €
          </p>
        </div>

        <div>
          <div hlmProgress [value]="redeemedPercent">
            <div hlmProgressIndicator></div>
          </div>
          <div class="mt-1.5 flex items-center justify-between gap-2">
            <p hlmMuted class="text-xs">{{ formatAmount(redeemedAmount) }} € eingelöst</p>
            <p hlmMuted class="text-xs tabular-nums">{{ redeemedPercent }} %</p>
          </div>
        </div>
      </div>

      <!-- ── Actions ── -->
      <div class="space-y-2">
        @if (voucher.status === 'active' && canRedeem) {
          <button hlmBtn class="w-full" [disabled]="isRedeeming" (click)="redeemFull()">
            @if (isRedeeming) {
              <hlm-spinner />
            }
            Restbetrag {{ formatAmount(voucher.amount) }} € einlösen
          </button>
        }

        <div class="flex gap-2">
          @if (voucher.status === 'active' && canRedeem && !showPartialForm) {
            <button hlmBtn variant="ghost" class="flex-1" (click)="showPartialForm = true">
              Teilbetrag einlösen
            </button>
          }
          @if (isOwnVoucher) {
            <button
              hlmBtn
              variant="outline"
              class="flex-1"
              [disabled]="isDownloading"
              (click)="downloadPdf()">
              @if (isDownloading) {
                <hlm-spinner />
              } @else {
                <ng-icon name="lucideDownload" />
              }
              PDF
            </button>
          }
        </div>

        @if (voucher.status === 'active' && canRedeem) {
          @if (showPartialForm) {
            <form
              [formGroup]="partialRedemptionForm"
              (ngSubmit)="redeemPartial()"
              class="border-border space-y-2 rounded-lg border p-3">
              <label hlmFieldLabel for="partial-amount">Teilbetrag einlösen</label>
              <div class="flex items-start gap-2">
                <hlm-input-group class="flex-1">
                  <input
                    hlmInputGroupInput
                    id="partial-amount"
                    type="text"
                    inputmode="decimal"
                    autocomplete="off"
                    formControlName="amount"
                    [maskito]="euroMask"
                    placeholder="0,00"
                    [attr.aria-invalid]="showPartialError ? 'true' : null" />
                  <hlm-input-group-addon align="inline-end">
                    <span hlmInputGroupText>€</span>
                  </hlm-input-group-addon>
                </hlm-input-group>
                <button
                  hlmBtn
                  variant="outline"
                  type="submit"
                  [disabled]="isRedeeming || partialRedemptionForm.invalid">
                  Einlösen
                </button>
              </div>
              @if (showPartialError) {
                <p class="text-destructive text-xs">
                  Bitte einen Betrag zwischen 0,01 € und {{ formatAmount(voucher.amount) }} €
                  angeben.
                </p>
              }
            </form>
          }
        }
      </div>

      <!-- ── Product ── -->
      @if (isProductVoucher() && voucher.voucher_product_data) {
        <div hlmItem variant="outline">
          <div hlmItemMedia variant="image">
            @if (getProductImageUrl()) {
              <img [src]="getProductImageUrl()" [alt]="voucher.voucher_product_data.name" />
            } @else {
              <ng-icon name="lucideGift" />
            }
          </div>
          <div hlmItemContent>
            <span hlmItemTitle>{{ voucher.voucher_product_data.name }}</span>
            @if (voucher.voucher_product_data.description) {
              <span hlmItemDescription>{{ voucher.voucher_product_data.description }}</span>
            }
          </div>
          <div hlmItemActions class="text-sm font-medium tabular-nums">
            {{ formatAmount(voucher.voucher_product_data.price) }} €
          </div>
        </div>
      }

      @if (voucher.is_internal) {
        <div hlmAlert variant="warning">
          <ng-icon name="lucideBan" />
          <p hlmAlertTitle>Intern erstellt</p>
          <p hlmAlertDescription>
            Keine Zahlung, keine Gebühren. Nicht Teil der Rechnungsübersicht und der
            Umsatzstatistik.
            @if (voucher.internal_note) {
              <span class="mt-1 block">Notiz: {{ voucher.internal_note }}</span>
            }
          </p>
        </div>
      }

      <!-- ── Facts, collapsed: rarely the reason the dialog was opened ── -->
      <div hlmAccordion type="single">
        <div hlmAccordionItem class="border-b-0">
          <h3 hlmAccordionHeader>
            <button hlmAccordionTrigger>
              Kunde & Gutschein-Daten
              <ng-icon name="lucideChevronDown" />
            </button>
          </h3>
          <hlm-accordion-content>
            <dl class="divide-border divide-y pb-1 text-sm">
              <div class="flex items-start justify-between gap-4 py-2">
                <dt hlmMuted class="shrink-0 text-xs">Kunde</dt>
                <dd class="min-w-0 text-end">
                  <span class="block truncate">{{ voucher.recipient_name || '–' }}</span>
                  <span class="text-muted-foreground block truncate text-xs">
                    {{ voucher.recipient_email }}
                  </span>
                </dd>
              </div>

              <div class="flex items-center justify-between gap-4 py-2">
                <dt hlmMuted class="shrink-0 text-xs">Erstellt</dt>
                <dd class="text-end">{{ formatDate(voucher.created_at) }}</dd>
              </div>

              @if (voucher.expires_at) {
                <div class="flex items-center justify-between gap-4 py-2">
                  <dt hlmMuted class="shrink-0 text-xs">Gültig bis</dt>
                  <dd class="text-end">{{ formatDate(voucher.expires_at) }}</dd>
                </div>
              }

              @if (voucher.redeemed_at) {
                <div class="flex items-center justify-between gap-4 py-2">
                  <dt hlmMuted class="shrink-0 text-xs">Eingelöst</dt>
                  <dd class="text-end">{{ formatDate(voucher.redeemed_at) }}</dd>
                </div>
              }

              @if (voucher.refunded_at) {
                <div class="flex items-center justify-between gap-4 py-2">
                  <dt hlmMuted class="shrink-0 text-xs">Erstattet</dt>
                  <dd class="text-end">{{ formatDate(voucher.refunded_at) }}</dd>
                </div>
              }

              @if (voucher.personal_message) {
                <div class="py-2">
                  <dt hlmMuted class="mb-1 text-xs">Persönliche Nachricht</dt>
                  <dd class="text-muted-foreground italic">„{{ voucher.personal_message }}"</dd>
                </div>
              }
            </dl>
          </hlm-accordion-content>
        </div>
      </div>
    </div>

    <!-- ═══════════════ Historie ═══════════════ -->
    <div hlmTabsContent="history" class="min-h-80 pt-3">
      <ol class="relative">
        @for (event of timeline; track $index) {
          <li class="flex gap-3">
            <!-- Rail: dot plus the connector down to the next event -->
            <div class="flex flex-col items-center">
              <span
                class="ring-background flex size-7 shrink-0 items-center justify-center rounded-full ring-4"
                [class]="dotClass(event.tone)">
                <ng-icon [name]="event.icon" class="text-[length:--spacing(3.5)]" />
              </span>
              @if (!$last) {
                <span class="bg-border w-px flex-1"></span>
              }
            </div>

            <div class="min-w-0 flex-1" [class.pb-5]="!$last">
              <div class="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                <p class="text-sm font-medium">{{ event.title }}</p>
                @if (event.amount) {
                  <p class="text-sm font-semibold tabular-nums">{{ event.amount }}</p>
                }
              </div>
              @if (event.date) {
                <p hlmMuted class="text-xs">{{ event.date }}</p>
              }
              @if (event.detail) {
                <p hlmMuted class="text-xs">{{ event.detail }}</p>
              }
              @if (event.balance) {
                <p hlmMuted class="text-xs tabular-nums">{{ event.balance }}</p>
              }
            </div>
          </li>
        }
      </ol>
    </div>
  </hlm-tabs>
}
